/**
 * How cards are ordered inside a board column.
 *
 * The server sorts so paging stays correct: the
 * first 20 of a column must be the first 20 in the chosen order, not 20 cards
 * sorted after the fact.
 *
 * Every sort breaks ties the same way — the next most useful date, then the
 * order label — and anything missing the date it sorts by goes last.
 */
import type { PortalBoardSort, PortalOrderCard } from "../types.ts";
import { daysBetween } from "../../../lib/partnerBoard/dates.ts";

export const DEFAULT_SORT: PortalBoardSort = "ship_by";

export const BOARD_SORTS: { value: PortalBoardSort; label: string }[] = [
  { value: "ship_by", label: "Ship by" },
  { value: "deadline", label: "Deadline" },
  { value: "work_on", label: "Work on date" },
  { value: "most_overdue", label: "Most overdue" },
  { value: "recently_sent", label: "Recently sent" },
];

export function isBoardSort(value: string | null): value is PortalBoardSort {
  return BOARD_SORTS.some((option) => option.value === value);
}

type SortableCard = Pick<PortalOrderCard, "label" | "shipBy" | "deadline" | "workOn" | "sentAt">;
type Compare = (a: SortableCard, b: SortableCard) => number;

/** Earliest first; null last. */
const byDate =
  (key: "shipBy" | "deadline" | "workOn"): Compare =>
  (a, b) =>
    (a[key] ?? "9999").localeCompare(b[key] ?? "9999");

const byLabel: Compare = (a, b) => a.label.localeCompare(b.label, undefined, { numeric: true });

const chain =
  (...compares: Compare[]): Compare =>
  (a, b) => {
    for (const compare of compares) {
      const result = compare(a, b);
      if (result !== 0) return result;
    }
    return 0;
  };

/** Days past the deadline; 0 when not overdue or undated. */
function daysOverdue(card: SortableCard, today: string): number {
  return card.deadline && card.deadline < today ? daysBetween(card.deadline, today) : 0;
}

function comparatorFor(sort: PortalBoardSort, today: string): Compare {
  switch (sort) {
    case "deadline":
      return chain(byDate("deadline"), byDate("shipBy"), byLabel);
    case "work_on":
      return chain(byDate("workOn"), byDate("shipBy"), byLabel);
    case "most_overdue":
      return chain((a, b) => daysOverdue(b, today) - daysOverdue(a, today), byDate("shipBy"), byLabel);
    case "recently_sent":
      return chain((a, b) => b.sentAt.localeCompare(a.sentAt), byDate("shipBy"), byLabel);
    case "ship_by":
    default:
      return chain(byDate("shipBy"), byDate("deadline"), byLabel);
  }
}

/** A sorted copy; the input is left alone. */
export function sortCards<T extends SortableCard>(cards: readonly T[], sort: PortalBoardSort, today: string): T[] {
  return [...cards].sort(comparatorFor(sort, today));
}
